import React from 'react';

import { TOOL_IDS } from '@adsk/alloy-provider';
import { MoneyIcon } from '@adsk/alloy-react-icon';
import theme from '@adsk/alloy-react-theme';
import { TabTitle } from '@adsk/exo-skeleton-react';

import AddRelationButton from './AddRelationButton';
import ActivityCard from './CostActivityCard';
import { CostProvider, useCostContext } from './CostProvider';
import CreateNewEntityPanel from './Create';
import CustomDataView from './CustomDataView';
import EntityBlock from './EntityBlock';
import EntityMenuItem from './EntityCellItem';
import { useTranslation } from './i18n';
import CustomPickerError from './PickerModalError';
import SelectedCount from './SelectedCount';
import {
  withProps,
  MAPPING_ICON,
  MAPPING_TOOL_ID,
  MAPPING_SORT,
  MAPPING_META_TYPE,
  MAPPING_KEY,
} from '../helpers/consts';
import costService from '../helpers/costSdk';
import { customizedType } from '../helpers/localeHelper';

const NestedOptionMenuLabel = () => {
  const { t } = useTranslation();
  return t('COST');
};

export const generatePlugin = (type = '') => {
  return {
    metadata: {
      domain: 'autodesk-bim360-cost',
      type: MAPPING_META_TYPE[type] || type,
    },
    renderIcon: MAPPING_ICON[type],
    toolId: TOOL_IDS[MAPPING_TOOL_ID[type]],
    showSearchBox: true,
    is3LOSupported: true,
    modalProps: {
      width: 800,
      height: 700,
    },
    Provider: CostProvider,
    nestedOptions: {
      label: <NestedOptionMenuLabel />,
      renderIcon: (p) => (
        <MoneyIcon
          {...p}
          style={{ ...p.style, color: theme.colors.charcoal700 }}
        />
      ),
      key: 'cost',
      nestedCategoryKey:
        MAPPING_TOOL_ID[type] === 'COST'
          ? 'cost'
          : MAPPING_TOOL_ID[type] === 'BUDGET'
            ? 'budget'
            : 'changeOrder',
      hideSubMenuItemIcon: true,
    },
    PluralText: () => {
      const { t } = useTranslation();
      const { terminologies } = useCostContext();
      return customizedType(t, type, terminologies);
    },
    PickerModalTitle: withProps(({ showTabTitle = true }) => {
      const { t } = useTranslation();
      const { terminologies } = useCostContext();
      const value = t('ADD', {
        type: customizedType(t, type, terminologies),
      });

      return (
        <>
          {showTabTitle && <TabTitle value={value} />}
          {value}
        </>
      );
    }, type),
    AddRelationshipsButtonText: withProps(AddRelationButton, type),
    SelectedCount: withProps(SelectedCount, type),
    queryEntities: async (query) => {
      const {
        projectId,
        offset = 0,
        filters: { searchExpression },
      } = query;

      const pageLimit = 100;

      const params = {
        limit: pageLimit,
        offset,
        sort: MAPPING_SORT[type],
        ...(['costPayApp', 'budgetPayApp'].includes(type)
          ? {
              associationType:
                type === 'costPayApp' ? 'Contract' : 'MainContract',
            }
          : {}),
      };

      if (searchExpression) {
        params.q = JSON.stringify([
          {
            column: 'name',
            type: 'string',
            value: searchExpression,
            operator: 'contains',
          },
          {
            logic: 'or',
            column: MAPPING_KEY[type],
            type: 'string',
            value: searchExpression,
            operator: 'contains',
          },
        ]);
      }

      const response = await costService.queryEntities({
        projectId,
        params,
        type,
      });

      const nextOffset = pageLimit ? offset + pageLimit : undefined;
      return {
        data: response.results,
        nextPageQuery:
          nextOffset && nextOffset < response.pagination.totalResults
            ? { ...query, offset: nextOffset || offset }
            : undefined,
      };
    },
    getEntitiesByIds: async ({ ids, projectId }) => {
      try {
        const response = await costService.getEntitiesByIds({
          ids,
          projectId,
          type,
        });
        return { data: response.results };
      } catch (e) {
        if (costService.shouldIgnoreError(e)) {
          return { data: [] };
        }

        throw e;
      }
    },
    canCreate: ({ projectId }) => costService.canCreate({ projectId, type }),
    CreateNewEntityPanel: withProps(CreateNewEntityPanel, type),
    EntityBlock: withProps(EntityBlock, type),
    EntityMenuItem: withProps(EntityMenuItem, type),
    CustomDataView: withProps(CustomDataView, type),
    CustomPickerError: withProps(CustomPickerError, type),
    ActivityCard: withProps(ActivityCard, type),
  };
};
